import React, {useEffect} from 'react';
import {NextSeo} from 'next-seo';
import dynamic from 'next/dynamic';
import {useRouter} from 'next/router';
import Image from 'next/image';
import {Button, BUTTON_THEMES} from 'ht-styleguide';

/* Client Stores */
import {useResponsiveIsMobile} from '@store/client/store.responsive';

/* Components */
import LazyComponent from '@components/LazyComponent';
import Maybe from '@components/Maybe';
import {DevlinkSkuCategoryPageHero} from '@devlink/DevlinkSkuCategoryPageHero';
import SpaceY from '@components/UI/Space/Space.y-axis';
import {DevlinkSkuBrowserByCategoryTabs} from '@devlink/DevlinkSkuBrowserByCategoryTabs';
import {DevlinkTabItem} from '@devlink/DevlinkTabItem';
import {DevlinkSkuGrid4Columns} from '@devlink/DevlinkSkuGrid4Columns';
import {DevlinkTabItemActive} from '@devlink/DevlinkTabItemActive';
import {DevlinkSkuCardMinimal} from '@devlink/DevlinkSkuCardMinimal';

/* Utils */
import {calculateMinHeight} from '@utils/helpers';

/* Types */
import {description, title} from '@scenes/ServicesCategoryPage/servicesCategoryPage.meta';
import {TServicesCategoryProps} from 'types/serviceCategory.types';
import {skuPath} from '@constants/paths';
import {TActiveGroup, TServiceCardGroup} from '@scenes/ServicesCategoryPage/serviceCategoryPage.types';

import styles from './servicesCategoryPage.module.scss';
import globalStyles from '@components/base.module.scss';

/* Components Dynamic */
const CustomerReviewsDynamic = dynamic(() => import('@components/UI/Reviews'), {ssr: false});
const DevlinkContentBlockRightTextDynamic = dynamic(() =>
  import('@devlink/DevlinkContentBlockRightText').then(module => module.DevlinkContentBlockRightText),
);
const DevlinkContentBlockLeftTextDynamic = dynamic(() =>
  import('@devlink/DevlinkContentBlockLeftText').then(module => module.DevlinkContentBlockLeftText),
);
const DevLinkStatsSectionDynamic = dynamic(
  () => import('@devlink/DevlinkSkuCategoryStatsSection').then(module => module.DevlinkSkuCategoryStatsSection),
  {
    ssr: false,
  },
);

const ServicesSkuPage = (props: TServicesCategoryProps) => {
  /* Hooks */
  const router = useRouter();
  /* Local State */
  const [activeTab, setActiveTab] = React.useState<number | null>(() => props?.category?.tabGroupings?.[0].id);
  const [activeGroup, setActiveGroup] = React.useState<TActiveGroup | null>(null);

  /* Client Store */
  const isMobile = useResponsiveIsMobile();

  /* -------- START METHODS --------------------- */

  const smoothScrollToTop = () => {
    window.scrollTo({
      top: 100,
      behavior: 'smooth',
    });
  };

  /* Redirect to the Sku Page */
  const onHandleBookSkuClick = (seoName: string) => {
    router.push(skuPath(seoName));
  };

  /**
   * Panel/Grid display
   * @param {TServiceCardGroup[]} skus
   * @returns {React.JSX.Element}
   */
  const generateGridPanels = (skus: TServiceCardGroup[]): React.JSX.Element => (
    <>
      {skus
        .filter(sku => sku.id)
        .map(sku => {
          if (!sku.image || sku.image.includes('missing')) {
            sku.image = '/_nextassets_/icons/sku-icon-default.png';
          }

          return (
            <DevlinkSkuCardMinimal
              skuName={sku.name}
              showSkuIcon={false}
              showSkuIconSlot={true}
              skuIconSlot={<Image src={sku.image} alt={`Expert service & support for ${sku.name}`} layout="responsive" width={48} height={48} />}
              action={{onClick: () => onHandleBookSkuClick(sku.seo_name)}}
              key={sku.name}
            />
          );
        })}
    </>
  );

  /* -------- END METHODS --------------------- */

  /* Constants */
  const {category: {reviews = [], name = '', seoName = '', id = 0, servicesCompletedCount = 0, currentPage = {}, tabGroupings = []} = {}} = props;
if (seoName === "around-the-home") {
  console.log(props)
}
  /* When tab changes, get the associated skus and set state */
  useEffect(() => {
    const defaultActiveGroup = [tabGroupings].flat().find(group => group.name === activeTab) || tabGroupings[0];
    setActiveGroup(defaultActiveGroup);
  }, [activeTab, id]);

  useEffect(() => {
    setActiveTab(props?.category?.tabGroupings?.[0].id);
  }, [props?.category?.tabGroupings?.[0].id]);

  if (!currentPage) return null;

  /* This is for CLS for performance for skus panel display */
  const sizingKey = String(isMobile);
  const sizing = {
    true: {baseLngth: 83, base: 32, groupHeight: 56, itemHeight: 96},
    false: {baseLngth: 110, base: 56, groupHeight: 28, itemHeight: 212},
  };
  const baseTopSize = tabGroupings.length > 1 ? sizing[sizingKey].baseLngth : sizing[sizingKey].base;
  const baseBodySize = calculateMinHeight(activeGroup?.types || [], {
    groupHeight: sizing[sizingKey].groupHeight,
    itemHeight: sizing[sizingKey].itemHeight,
    byPredicate: sku => sku.id,
    isMobile,
  });
  const baseTotalSize = `${baseTopSize + baseBodySize}px`;

  return (
    <>
      <NextSeo title={title(name)} description={description(name, seoName)} />
      <div id="top" />
      <div style={{width: '100%'}}>
        <DevlinkSkuCategoryPageHero
          showCategoryImage={false}
          skuCategoryImage=""
          skuCategoryName={name}
          showImageSlot={true}
          imageSlot={
            <div className={styles.heroImage}>
              <Image
                src={`/_nextassets_/images/categories/page/skucategory-hero-${seoName}.webp`}
                alt={`${name} Services as soon as today`}
                quality={60}
                priority
                width={isMobile ? 200 : 560}
                height={isMobile ? 83 : 233}
                style={{
                  objectFit: 'contain',
                }}
              />
            </div>
          }
        />
      </div>
      <SpaceY classes="paddingTop-small1" />
      <div style={{minHeight: baseTotalSize}}>
        <DevlinkSkuBrowserByCategoryTabs
          showSectionTitle={false}
          tabMenu={
            <>
              {tabGroupings.length > 1 &&
                tabGroupings.map((tab: TActiveGroup) =>
                  tab.id === activeTab ? (
                    <DevlinkTabItemActive
                      tabLabel={tab.name}
                      tabAction={{
                        onClick: () => setActiveTab(tab.id),
                      }}
                    />
                  ) : (
                    <DevlinkTabItem
                      tabLabel={tab.name}
                      tabAction={{
                        onClick: () => setActiveTab(tab.id),
                      }}
                    />
                  ),
                )}
            </>
          }
          showTabMenu={true}
          skuGrids={
            <>
              {activeGroup?.types?.map(tab => (
                <DevlinkSkuGrid4Columns
                  displayGridTitle
                  displayGridProminentTitle={false}
                  gridTitle={tab.name}
                  skuGrid={generateGridPanels(tab.skus)}
                  key={tab.name}
                />
              ))}
            </>
          }
        />
      </div>
      <Maybe condition={servicesCompletedCount}>
        <SpaceY classes="paddingTop-medium" />
        <div style={{minHeight: `${isMobile ? 160 : 204}px`}}>
          <DevLinkStatsSectionDynamic skuCategoryName={name} skuCategoryCompletionCount={servicesCompletedCount} />
        </div>
      </Maybe>
      <SpaceY classes="paddingTop-huge1" />
      <LazyComponent minHeight={`${isMobile ? 296 : 400}px`} loader={false}>
        <DevlinkContentBlockLeftTextDynamic
          showEyebrowText={false}
          eyebrowText=""
          mainTitle="Service as soon as today"
          showBodyText={true}
          bodyText="In-home service from 7am-9pm. Book online or by phone in minutes for service as soon as today!"
          showButtonGroup={false}
          buttonGroup=""
          showImageFill={false}
          imageFill=""
          showImageSlot={true}
          imageSlot={
            <div className={globalStyles.contentBlockWrapper}>
              <Image
                src={`/_nextassets_/images/categories/page/skucategory-${seoName}-img-assoonastoday.webp`}
                alt={`${name} Service as soon as today`}
                fill
                style={{
                  objectFit: 'cover',
                }}
              />
            </div>
          }
        />
      </LazyComponent>
      <Maybe condition={reviews.length > 0}>
        <SpaceY classes="paddingTop-large" />
        <LazyComponent minHeight={`${isMobile ? 250 : 450}px`} loader={false}>
          <CustomerReviewsDynamic
            headerText="What our customers say"
            className="paddingX-medium"
            reviews={reviews}
            headerStyles="text-align-center"
            removeBackground={true}
          />
        </LazyComponent>
      </Maybe>
      <LazyComponent minHeight={isMobile ? '300px' : '450px'} loader={false}>
        <SpaceY classes="paddingTop-large" />
        <DevlinkContentBlockRightTextDynamic
          showEyebrowText={false}
          eyebrowText=""
          mainTitle="Ready to book your service?"
          showBodyText={false}
          bodyText=""
          showButtonGroup={true}
          buttonGroup={
            <Button className={styles.catPageButtonGroup} theme={BUTTON_THEMES.primary} onClick={smoothScrollToTop}>
              Get Started
            </Button>
          }
          showImageFill={false}
          imageFill=""
          showImageSlot={true}
          imageSlot={
            <div style={{position: 'relative', width: '100%', height: '100%'}}>
              <Image
                src={`/_nextassets_/images/categories/page/skucategory-${seoName}-img-bottomcta.webp`}
                alt={`${name} Service as soon as today`}
                fill
                style={{
                  objectFit: 'cover',
                }}
              />
            </div>
          }
        />
        <SpaceY classes="paddingTop-huge1" />
      </LazyComponent>
    </>
  );
};

export default ServicesSkuPage;
